import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import { useCallback } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { useWebContainer } from "@/features/web-container/context";
import { useSearchParams } from "react-router-dom";
import { ReploError } from "schemas/errors";

import {
  useChatStoreActions,
  useIsChatLoading,
} from "../../chat/store/ChatStoreContext";
import { useModifiersStore } from "../store/ModifiersStoreContext";

class ModifierParameterError extends ReploError {}
class ModifierSelectedElementError extends ReploError {}

export const useModifiers = (): {
  applyModifier: (modifier: UniversalModifierValue) => Promise<void>;
  isLoading: boolean;
} => {
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const { mountAdditionalFiles } = useWebContainer();
  const isChatLoading = useIsChatLoading();
  const { setIsChatLoading } = useChatStoreActions();
  const { selectedElement, actions: modifierActions } = useModifiersStore();

  const applyModifierMutation = trpc.modifiers.modify.useMutation();

  const applyModifier = useCallback(
    async (modifier: UniversalModifierValue) => {
      if (!projectId || !pageId || !selectedElement) {
        throw new ModifierParameterError({
          message: "Missing required parameters for modifier",
          additionalData: {
            projectId,
            pageId,
            hasSelectedElement: Boolean(selectedElement),
          },
        });
      }

      if (
        selectedElement.line === null ||
        selectedElement.column === null ||
        !selectedElement.file
      ) {
        throw new ModifierSelectedElementError({
          message: "Selected element is missing position or file information",
          additionalData: {
            line: selectedElement.line,
            column: selectedElement.column,
            hasFile: Boolean(selectedElement.file),
          },
        });
      }

      setIsChatLoading(true);

      try {
        const result = await applyModifierMutation.mutateAsync({
          projectId,
          fileId: pageId,
          elementSelector: {
            line: selectedElement.line,
            column: selectedElement.column,
          },
          modifier,
        });

        if (mountAdditionalFiles && result.fileTree) {
          try {
            await mountAdditionalFiles(result.fileTree);
          } catch (webContainerError) {
            console.warn(
              "Web container file tree mounting failed (non-critical):",
              webContainerError,
            );
          }
        }

        await trpcUtils.nextJSProject.getPage.fetch({
          projectId: projectId,
          id: pageId,
        });

        if (modifier.type === "delete") {
          modifierActions.resetToDefaults();
          modifierActions.setDisplayMode("select");
        }
      } finally {
        setIsChatLoading(false);
        // NOTE (Kurt, 2025-07-21): Invalidate page component to get trpc to
        // cache the updated page component.
        void trpcUtils.nextJSProject.getPage.invalidate({
          projectId: projectId!,
          id: pageId,
        });
      }
    },
    [
      projectId,
      pageId,
      selectedElement,
      applyModifierMutation,
      setIsChatLoading,
      mountAdditionalFiles,
      modifierActions,
    ],
  );

  return {
    applyModifier,
    isLoading: isChatLoading,
  };
};
